import { CSSProperties, ReactNode, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Brain,
  Film,
  GraduationCap,
  HelpCircle,
  Layers,
  LayoutGrid,
  Lock,
  Mic,
  ShieldCheck,
  ShoppingCart,
  TrendingUp,
  Users,
  Video,
  Zap,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import ServiceCard from "@/components/ServiceCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import img129 from "@/assets/image/pdf/img129.webp";
import img148 from "@/assets/image/pdf/img148.webp";
import img261 from "@/assets/image/pdf/img261.webp";
import img65 from "@/assets/image/pdf/img65.webp";
import { getLangInfo, withLangPrefix } from "@/i18n";

const homeSectionImages = {
  clients: "/images/home/clients.avif",
  faq: "/images/home/faq.avif",
};

interface ArchitectureItem {
  num: string;
  title: string;
  desc: string;
}

interface FaqItem {
  q: string;
  summary: string;
  details: string[];
}

interface HeroStat {
  label: string;
  value: string;
}

interface ProofTextItem {
  title: string;
  desc: string;
}

interface ServiceCardText {
  title: string;
  description: string;
  linkText: string;
}

interface CompareItem {
  old: string;
  oldDo: string;
  us: string;
}

function Reveal({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  return (
    <div className="reveal-up" style={{ animationDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

const Index = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { lang } = getLangInfo(pathname);
  const withLang = (path: string) => withLangPrefix(path, lang);
  const [activeProof, setActiveProof] = useState(0);

  const architectureItems = t("home.architecture.items", { returnObjects: true }) as ArchitectureItem[];
  const faqItems = t("home.faq.items", { returnObjects: true }) as FaqItem[];
  const heroHighlights = t("home.hero.highlights", { returnObjects: true }) as string[];
  const heroStats = t("home.hero.stats", { returnObjects: true }) as HeroStat[];
  const proofTextItems = t("home.proof.items", { returnObjects: true }) as ProofTextItem[];
  const serviceCardTexts = t("home.services.cards", { returnObjects: true }) as ServiceCardText[];

  const architectureIcons = [TrendingUp, ShieldCheck, Users, LayoutGrid, Layers];

  const proofMedia = [
    { image: img129, crop: "center" as const },
    { image: img261, crop: "center" as const },
    { image: img148, crop: "center" as const },
    { image: img65, crop: "top" as const },
  ];

  const proofReel = useMemo(
    () =>
      proofTextItems.map((item, i) => ({
        ...item,
        image: proofMedia[i]?.image ?? img129,
        crop: proofMedia[i]?.crop ?? "center",
      })),
    [proofTextItems],
  );

  const serviceCards = [
    { icon: GraduationCap, link: withLang("/courses"), delay: 0 },
    { icon: Video, link: withLang("/operations"), delay: 0.05 },
    { icon: ShoppingCart, link: withLang("/ecommerce"), delay: 0.1 },
    { icon: Lock, link: withLang("/certification"), delay: 0.15 },
    { icon: Brain, link: withLang("/ip-incubation"), delay: 0.2 },
    { icon: Layers, link: withLang("/matrix"), delay: 0.25 },
    { icon: Users, link: withLang("/team-expression"), delay: 0.3 },
    { icon: Mic, link: withLang("/interview"), delay: 0.35 },
    { icon: Film, link: withLang("/activity"), delay: 0.4 },
    { icon: LayoutGrid, link: withLang("/web-dev"), delay: 0.45 },
  ];

  return (
    <div className="home-experience">
      <section
        id="hero"
        data-home-section
        className="hero-gradient-bg min-h-[90vh] flex items-center relative overflow-hidden"
      >
        <div className="absolute inset-0 ttk-gridlines opacity-20" aria-hidden="true" />
        <div className="absolute inset-0 ttk-hero-scanline pointer-events-none" aria-hidden="true" />
        <div className="hero-particles" aria-hidden="true">
          {Array.from({ length: 16 }).map((_, i) => (
            <span key={i} style={{ "--i": i } as CSSProperties} />
          ))}
        </div>

        <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 ttk-section-tag mb-6 px-4 py-2 rounded-full reveal-up">
                <Zap className="w-3 h-3" /> {t("home.hero.badge")}
              </div>

              <h1 className="hero-title ttk-hero-title mb-6 reveal-up reveal-delay-1">
                {t("home.hero.titleLine1")}
                <br />
                <span className="gradient-gold-text">{t("home.hero.titleEmphasis")}</span>
              </h1>

              <p className="ttk-hero-lead mb-8 max-w-2xl reveal-up reveal-delay-2">
                <span className="ttk-hero-beat">{t("home.hero.description")}</span>
              </p>

              <div className="hero-highlight-grid mb-8">
                {heroHighlights.map((item, index) => (
                  <div
                    key={item}
                    className="hero-highlight-item ttk-card reveal-up"
                    style={{ animationDelay: `${0.2 + index * 0.08}s` }}
                  >
                    {item}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="flex reveal-up reveal-delay-3">
                  <Link
                    to={withLang("/contact")}
                    className="ttk-cta-primary inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 text-base sm:px-8 sm:py-4 sm:text-lg rounded-full font-semibold text-center"
                  >
                    {t("home.hero.ctaPrimary")}
                  </Link>
                </div>
                <div className="flex reveal-up reveal-delay-4">
                  <a
                    href="#architecture"
                    className="ttk-cta-outline inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 text-base sm:px-8 sm:py-4 sm:text-lg rounded-full font-semibold text-center"
                  >
                    {t("home.hero.ctaSecondary")}
                  </a>
                </div>
              </div>

              <div className="hero-stats mt-8">
                {heroStats.map((item, index) => (
                  <div
                    key={item.label}
                    className="hero-stat-item ttk-card reveal-up"
                    style={{ animationDelay: `${0.35 + index * 0.08}s` }}
                  >
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:flex lg:col-span-5 justify-end reveal-up reveal-delay-4">
              <div className="hero-frame w-full max-w-[390px] rounded-2xl border border-border/70 bg-card/50 backdrop-blur p-3 shadow-2xl relative">
                <img
                  src={img65}
                  alt={t("home.hero.imageAlt")}
                  width={1126}
                  height={789}
                  loading="lazy"
                  decoding="async"
                  sizes="(min-width: 1024px) 390px, 0px"
                  className="w-full h-[560px] object-contain object-top rounded-xl ttk-hero-breathe"
                />
                <div className="hero-float-pill hero-float-pill-a">{t("home.hero.floatA")}</div>
                <div className="hero-float-pill hero-float-pill-b">{t("home.hero.floatB")}</div>
                <div className="ttk-hero-edge" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="scroll-cue hidden md:flex reveal-up reveal-delay-5">{t("home.hero.scrollCue")}</div>
        </div>
      </section>

      <section className="py-24 md:py-32 hero-gradient-bg ttk-final-shell">
        <div className="container mx-auto px-4 text-center relative z-10">
          <Reveal>
            <p className="text-lg text-muted-foreground mb-4">{t("home.finalCta.pretitle")}</p>
            <h2 className="ttk-section-title font-bold mb-4">{t("home.finalCta.title")}</h2>
            <p className="ttk-section-subtitle mb-10 max-w-3xl mx-auto">{t("home.finalCta.lead")}</p>

            <div className="ttk-final-grid max-w-4xl mx-auto mb-10 text-left">
              <div className="ttk-card rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4">{t("home.finalCta.fromTitle")}</h3>
                <div className="flex flex-wrap gap-2">
                  {(t("home.finalCta.fromItems", { returnObjects: true }) as string[]).map((item) => (
                    <span key={item} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm ttk-chip">
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              <div className="ttk-card rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4">{t("home.finalCta.missingTitle")}</h3>
                <div className="flex flex-wrap gap-2">
                  {(t("home.finalCta.missingItems", { returnObjects: true }) as string[]).map((item) => (
                    <span key={item} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm ttk-chip">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <p className="ttk-section-subtitle mb-10 max-w-xl mx-auto">{t("home.finalCta.description")}</p>
            <div className="inline-flex items-center gap-4 ttk-cta-strip px-3 py-2.5 sm:px-4 sm:py-3 rounded-full">
              <Link
                to={withLang("/contact")}
                className="inline-flex items-center gap-2 ttk-cta-primary px-6 py-2.5 text-sm sm:px-8 sm:py-3 sm:text-base rounded-full font-semibold"
              >
                {t("home.finalCta.button")} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#proof" className="inline-flex items-center gap-2 ttk-cta-outline px-5 py-2.5 rounded-full text-xs sm:px-6 sm:py-3 sm:text-sm font-semibold">
                {t("home.hero.ctaSecondary")}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="ttk-divider" />

      <section id="architecture" data-home-section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <SectionHeading tag={t("home.architecture.tag")} title={t("home.architecture.title")} subtitle={t("home.architecture.subtitle")} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {architectureItems.map((item, i) => {
              const Icon = architectureIcons[i % architectureIcons.length];
              return (
                <Reveal key={item.num} delay={i * 0.06}>
                  <div className="ttk-card rounded-2xl p-6 interactive-card">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg ttk-icon-badge flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <span className="text-xs text-foreground font-semibold">{item.num}</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <div className="ttk-divider" />

      <section id="services" data-home-section className="py-20 md:py-28 ttk-services-shell">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.2fr] gap-10 items-end mb-12">
            <div>
              <div className="ttk-services-kicker text-xs mb-4">{t("home.services.tag")}</div>
              <h2 className="ttk-services-title font-bold mb-4">{t("home.services.title")}</h2>
              <p className="ttk-services-lead max-w-xl">{t("home.services.subtitle")}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Link to={withLang("/contact")} className="ttk-cta-primary px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base font-semibold text-center">
                {t("home.hero.ctaPrimary")}
              </Link>
              <a href="#proof" className="ttk-cta-outline px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-sm sm:text-base font-semibold text-center">
                {t("home.hero.ctaSecondary")}
              </a>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceCards.map((card, i) => (
              <ServiceCard
                key={card.link}
                icon={card.icon}
                title={serviceCardTexts[i]?.title ?? ""}
                description={serviceCardTexts[i]?.description ?? ""}
                link={card.link}
                linkText={serviceCardTexts[i]?.linkText ?? ""}
                delay={card.delay}
              />
            ))}
          </div>
        </div>
      </section>

      <div className="ttk-divider" />

      <section id="proof" data-home-section className="py-20 md:py-28 proof-stage ttk-proof-shell">
        <div className="container mx-auto px-4">
          <SectionHeading tag={t("home.proof.tag")} title={t("home.proof.title")} />

          <div className="hidden md:block">
            <div className="proof-sticky">
              <div className="proof-layout">
                <div className="proof-copy">
                  <p className="proof-kicker ttk-proof-kicker">
                    {t("home.proof.shotPrefix")} {String(activeProof + 1).padStart(2, "0")}
                  </p>
                  <h3 className="ttk-proof-title font-semibold mb-3">{proofReel[activeProof]?.title}</h3>
                  <p className="ttk-proof-desc leading-7 max-w-md">{proofReel[activeProof]?.desc}</p>
                  <div className="proof-dots">
                    {proofReel.map((item, i) => (
                      <button
                        key={`${item.title}-${i}`}
                        type="button"
                        onClick={() => setActiveProof(i)}
                        className={`proof-dot ${activeProof === i ? "proof-dot-active" : ""}`}
                        aria-label={`${t("home.proof.dotAriaPrefix")} ${item.title}`}
                      />
                    ))}
                  </div>
                </div>

                <div className="proof-deck">
                  {proofReel.map((item, i) => (
                    <article
                      key={`${item.title}-${i}`}
                      className="proof-card ttk-proof-card relative overflow-hidden"
                      style={{
                        zIndex: proofReel.length - Math.abs(i - activeProof),
                        opacity: i === activeProof ? 1 : i < activeProof ? 0 : 0.22,
                        transform: `translate3d(${
                          i === activeProof ? 0 : i < activeProof ? -160 : 110 + (i - activeProof) * 18
                        }px, ${i === activeProof ? 0 : (i - activeProof) * 18}px, 0) scale(${
                          i === activeProof ? 1 : i < activeProof ? 0.82 : 0.9
                        })`,
                      }}
                    >
                      <div className="proof-media">
                        <img
                          src={item.image}
                          alt={item.title}
                          width={1230}
                          height={864}
                          loading="lazy"
                          decoding="async"
                          sizes="(min-width: 768px) 640px, 0px"
                          className={`w-full h-full object-cover ${item.crop === "top" ? "object-top" : "object-center"}`}
                        />
                      </div>
                      <div className="ttk-proof-caption p-4">
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                      <div className="ttk-proof-glow" aria-hidden="true" />
                    </article>
                  ))}
                  <div className="proof-scroll-hint">{t("home.proof.scrollHint")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:hidden reel-mobile">
            {proofReel.map((item, i) => (
              <article key={`${item.title}-${i}`} className="reel-card ttk-proof-card snap-center">
                <div className="reel-media">
                  <img
                    src={item.image}
                    alt={item.title}
                    width={1230}
                    height={864}
                    loading="lazy"
                    decoding="async"
                    sizes="78vw"
                    className={`w-full h-full object-cover ${item.crop === "top" ? "object-top" : "object-center"}`}
                  />
                </div>
                <div className="ttk-proof-caption p-4">
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="ttk-divider" />

      <section id="clients" data-home-section className="py-20 md:py-28 ttk-clients-shell">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
            <div>
              <div className="ttk-clients-tag text-xs mb-4">{t("home.clients.tag")}</div>
              <h2 className="ttk-section-title font-bold mb-5">{t("home.clients.title")}</h2>
              <Reveal>
                <figure className="mb-8 overflow-hidden rounded-2xl border border-border/70 interactive-card ttk-card">
                  <img
                    src={homeSectionImages.clients}
                    alt={t("home.clients.imageAlt")}
                    width={1600}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    sizes="(min-width: 1024px) 560px, 100vw"
                    className="w-full h-[240px] sm:h-[320px] object-cover"
                  />
                </figure>
              </Reveal>
              <div className="flex flex-wrap gap-2">
                {(t("home.clients.fitItems", { returnObjects: true }) as string[] | undefined)?.map((item) => (
                  <span key={item} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm ttk-clients-pill">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="ttk-clients-compare rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-4">{t("home.clients.compareTitle")}</h3>
              {(t("home.clients.compare", { returnObjects: true }) as CompareItem[] | undefined)?.map((row, idx) => (
                <div key={`${row.old}-${idx}`} className="ttk-clients-row">
                  <div>
                    <p className="text-sm text-muted-foreground">{row.old}</p>
                    <p className="text-base font-semibold text-foreground">{row.oldDo}</p>
                  </div>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">vs</span>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("home.clients.usLabel")}</p>
                    <p className="text-base font-semibold text-foreground">{row.us}</p>
                  </div>
                </div>
              ))}
              <div className="mt-6 pt-5 border-t border-border/60">
                <p className="text-sm text-muted-foreground mb-3">{t("home.clients.fitTitle")}</p>
                <div className="flex flex-wrap gap-2">
                  {(t("home.clients.fitItems", { returnObjects: true }) as string[] | undefined)?.map((item) => (
                    <span key={item} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm ttk-clients-pill">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-6 pt-5 border-t border-border/60">
                <p className="text-sm text-muted-foreground mb-3">{t("home.clients.notFitTitle")}</p>
                <div className="flex flex-wrap gap-2">
                  {(t("home.clients.notFitItems", { returnObjects: true }) as string[] | undefined)?.map((item) => (
                    <span key={item} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm ttk-clients-pill">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="ttk-divider" />
      <section id="faq" data-home-section className="py-20 md:py-28 ttk-faq-shell">
        <div className="container mx-auto px-4">
          <SectionHeading tag={t("home.faq.tag")} title={t("home.faq.title")} />
          <Reveal>
            <figure className="max-w-5xl mx-auto mb-10 overflow-hidden rounded-2xl border border-border/70 interactive-card ttk-card">
              <img
                src={homeSectionImages.faq}
                alt={t("home.faq.imageAlt")}
                width={1600}
                height={900}
                loading="lazy"
                decoding="async"
                sizes="(min-width: 1280px) 1024px, 100vw"
                className="w-full h-[230px] sm:h-[320px] object-cover"
              />
            </figure>
          </Reveal>
          <Reveal>
            <div className="max-w-4xl mx-auto ttk-faq-card rounded-2xl p-4 sm:p-6">
              <Accordion type="single" collapsible defaultValue="faq-0" className="w-full">
                {faqItems.map((item, i) => (
                  <AccordionItem key={item.q} value={`faq-${i}`} className="border-border/60 ttk-faq-item">
                    <AccordionTrigger className="text-left ttk-faq-trigger hover:no-underline">
                      <span className="inline-flex items-center gap-3">
                        <HelpCircle className="w-4 h-4 text-primary flex-shrink-0" />
                        {item.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 text-sm md:text-base text-muted-foreground leading-7 ttk-faq-answer">
                        <p className="text-foreground/95">{item.summary}</p>
                        {item.details.map((line, idx) => (
                          <p key={idx}>• {line}</p>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <div className="mt-6 pt-5 border-t border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">{t("home.faq.notSure")}</p>
                <Link to={withLang("/contact")} className="inline-flex items-center gap-2 ttk-cta-primary px-5 py-2.5 rounded-full text-sm font-semibold">
                  {t("home.faq.cta")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Index;
